import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { InputLabel } from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { IRootState } from "../../redux/store";
import { axiosGet } from "../../util/axios";
import { initContractListThunk } from "../../redux/staticInfo/thunk";
import { useDispatch } from "react-redux";
import { IRootThunkDispatch } from "../../redux/store";
import { ButtonTextGrid } from "../..";

interface PdfDownloadDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (reportType: string, selectedDate?: moment.Moment | null, contractId?: number | null, zoneIds?: number[] | null, isInternalReport?: boolean, contractName?: string | null, endDate?: moment.Moment | null, includePeopleCounter?: boolean) => void;
    reportType?: ReportType;
    defaultDate?: moment.Moment | null;
}

type ReportType =
    | "daily_cleaning"
    | "people_counter_129_25"
    | "people_counter_139_25"
    | "inspection_plastic_toilet"
    | "inspection_solar_toilet";

const reportTypesRequiringDate: ReportType[] = [
    "daily_cleaning",
];

const reportTypesRequiringMonth: ReportType[] = [
    "people_counter_129_25",
    "people_counter_139_25",
];

const reportTypesRequiringContract: ReportType[] = [
    "daily_cleaning",
];

const reportTypeToApiName: Record<ReportType, string> = {
    daily_cleaning: "DailyCleaning",
    people_counter_129_25: "129PeopleCounter",
    people_counter_139_25: "139PeopleCounter",
    inspection_plastic_toilet: "PlasticToiletDocx",
    inspection_solar_toilet: "SolarToiletDocx",
};

export default function PdfDownloadDialog({
    open,
    onClose,
    onSubmit,
    reportType,
    defaultDate,
}: PdfDownloadDialogProps) {
    const dispatch = useDispatch<IRootThunkDispatch>();
    const { contractList } = useSelector((state: IRootState) => state.staticInfoState);
    const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(moment());
    const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(moment());
    const [selectedMonth, setSelectedMonth] = useState<moment.Moment | null>(moment());
    const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
    const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
    const [selectedZoneIds, setSelectedZoneIds] = useState<number[]>([]);
    const [selectAllZones, setSelectAllZones] = useState<boolean>(false);
    const [zoneList, setZoneList] = useState<any[]>([]);
    const [allZoneList, setAllZoneList] = useState<any[]>([]);
    const [selectedReportType, setSelectedReportType] = useState<ReportType | "">(reportType || "");
    const [isInternalReport, setIsInternalReport] = useState<boolean>(false);
    const [includePeopleCounter, setIncludePeopleCounter] = useState<boolean>(true);
    
    const selectedReport = selectedReportType;

    const requiresDate = selectedReport !== "" && 
        reportTypesRequiringDate.includes(selectedReport as ReportType);
    
    const requiresMonth = selectedReport !== "" && 
        reportTypesRequiringMonth.includes(selectedReport as ReportType);

    const requiresContract = selectedReport !== "" && 
        reportTypesRequiringContract.includes(selectedReport as ReportType);

    const isInspectionReport = selectedReport === "inspection_plastic_toilet" || 
        selectedReport === "inspection_solar_toilet";
    const requiresDateInput = requiresDate && !isInspectionReport;
    const requiresMonthInput = requiresMonth;
    
    const isDailyCleaning = selectedReport === "daily_cleaning";
    
    const isPeopleCounter = selectedReport === "people_counter_129_25" || 
        selectedReport === "people_counter_139_25";

    const selectedContract = contractList.find((c) => c.contract_id === selectedContractId);
    const selectedContractHasPeopleCounter = isDailyCleaning && selectedContract
        ? selectedContract.image_type_id?.split(',').map((s: string) => s.trim()).includes('4')
        : false;

    useEffect(() => {
        if (reportType) {
            setSelectedReportType(reportType);
        } else {
            setSelectedReportType("");
        }
    }, [reportType, open]);

    useEffect(() => {
        if (open) {
            if (reportType) {
                setSelectedReportType(reportType);
            } else {
                setSelectedReportType("");
            }
            setSelectedDate(defaultDate || moment());
            setSelectedEndDate(defaultDate || moment());
            setSelectedMonth(moment());
            setSelectedContractId(null);
            setSelectedZoneId(null);
            setSelectedZoneIds([]);
            setSelectAllZones(false);
            setZoneList([]);
            setIsInternalReport(false);
            setIncludePeopleCounter(true);
        }
    }, [open, reportType, defaultDate]);

    useEffect(() => {
        if (open && (isDailyCleaning || isInspectionReport) && allZoneList.length === 0) {
            const loadAllZoneList = async () => {
                try {
                    const result = await axiosGet(`/api/contract/zone`);
                    if (result.data.success && result.data.zoneList) {
                        setAllZoneList(result.data.zoneList);
                    }
                } catch (error) {
                    console.error("無法載入區域列表", error);
                }
            };
            loadAllZoneList();
        }
    }, [open, isDailyCleaning, isInspectionReport, allZoneList.length]);

    useEffect(() => {
        if (selectedContractId && (isDailyCleaning || isInspectionReport)) {
            const filteredZones = allZoneList.filter(zone => zone.contract_id === selectedContractId);
            setZoneList(filteredZones);
            if (filteredZones.length === 1) {
                setSelectedZoneIds([filteredZones[0].zone_id]);
                setSelectAllZones(true);
            }
            setIncludePeopleCounter(true);
        } else {
            setZoneList([]);
            setSelectedZoneId(null);
            setSelectedZoneIds([]);
            setSelectAllZones(false);
        }
    }, [selectedContractId, isDailyCleaning, isInspectionReport, allZoneList]);

    const handleSelectAllZones = (checked: boolean) => {
        setSelectAllZones(checked);
        if (checked) {
            setSelectedZoneIds(zoneList.map(zone => zone.zone_id));
            setSelectedZoneId(null);
        } else {
            setSelectedZoneIds([]);
        }
    };

    const handleZoneSelect = (zoneId: number) => {
        setSelectAllZones(false);
        if (selectedZoneIds.includes(zoneId)) {
            setSelectedZoneIds(selectedZoneIds.filter(id => id !== zoneId));
        } else {
            setSelectedZoneIds([...selectedZoneIds, zoneId]);
        }
        setSelectedZoneId(null);
    };

    useEffect(() => {
        if (requiresDateInput && !selectedDate) {
            setSelectedDate(moment());
        }
        if (requiresMonthInput && !selectedMonth) {
            setSelectedMonth(moment());
        }
    }, [selectedReport, requiresDateInput, requiresMonthInput, selectedDate, selectedMonth]);

    useEffect(() => {
        if (open && contractList.length === 0) {
            const loadContractList = async () => {
                try {
                             const result = await axiosGet("/api/contract");
                    if (result.data.success && result.data.contractList) {
                        dispatch(initContractListThunk(result.data.contractList));
                    }
                } catch (error) {
                    console.error("無法載入合約列表", error);
                }
            };
            loadContractList();
        }
    }, [open, contractList.length, dispatch]);

    const handleReportTypeChange = (event: any) => {
        setSelectedReportType(event.target.value as ReportType);
    };

    const handleDateChange = (date: moment.Moment | null) => {
        setSelectedDate(date);
    };

    const handleEndDateChange = (date: moment.Moment | null) => {
        setSelectedEndDate(date);
    };

    const handleMonthChange = (date: moment.Moment | null) => {
        setSelectedMonth(date);
    };

    const handleSubmit = () => {
        if (selectedReport === "") {
            return;
        }
        
        if (requiresDateInput && !selectedDate) {
            return;
        }

        if (requiresMonthInput && !selectedMonth) {
            return;
        }

        if (requiresContract && !selectedContractId) {
            return;
        }

        if (isInspectionReport && !selectedContractId) {
            return;
        }

        if (isInspectionReport && (!selectedDate || !selectedEndDate || selectedDate.isAfter(selectedEndDate, 'day'))) {
            return;
        }

        const apiName = reportTypeToApiName[selectedReport as ReportType];
        const dateToSubmit = requiresMonthInput && selectedMonth 
            ? selectedMonth.clone().startOf('month')
            : (requiresDateInput || isInspectionReport) ? selectedDate : undefined;
        const contractIdToSubmit = (requiresContract || isInspectionReport) ? selectedContractId : null;
        const zoneIdToSubmit = (isDailyCleaning || isInspectionReport) ? (zoneList.length <= 1 ? (selectedZoneIds.length > 0 ? selectedZoneIds : null) : (selectAllZones ? null : (selectedZoneIds.length > 0 ? selectedZoneIds : (selectedZoneId ? [selectedZoneId] : null)))) : null;
        const selectedContractForName = contractList.find((contract) => contract.contract_id === selectedContractId);
        const contractNameToSubmit = selectedContractForName ? selectedContractForName.contract : null;
        
        handleClose();
        onSubmit(apiName, dateToSubmit, contractIdToSubmit, zoneIdToSubmit, isInspectionReport ? isInternalReport : undefined, contractNameToSubmit, isInspectionReport ? selectedEndDate : undefined, isDailyCleaning ? includePeopleCounter : undefined);
    };

    const handleClose = () => {
        setSelectedDate(moment());
        setSelectedEndDate(moment());
        setSelectedMonth(moment());
        setSelectedContractId(null);
        setSelectedZoneId(null);
        setSelectedZoneIds([]);
        setSelectAllZones(false);
        setZoneList([]);
        setAllZoneList([]);
        setSelectedReportType(reportType || "");
        setIsInternalReport(false);
        setIncludePeopleCounter(true);
        onClose();
    };

    const filteredContractList = isInspectionReport
        ? contractList.filter((contract) => {
            if (selectedReport === "inspection_plastic_toilet") {
                return contract.contract.includes("129/25") || contract.contract.includes("139/25");
            } else if (selectedReport === "inspection_solar_toilet") {
                return !contract.contract.includes("129/25") && !contract.contract.includes("139/25");
            }
            return true;
        })
        : (isDailyCleaning || isPeopleCounter)
        ? contractList
        : contractList;

    const canSubmit = selectedReport !== "" && 
        (!requiresDateInput || selectedDate !== null) &&
        (!requiresMonthInput || selectedMonth !== null) &&
        (!requiresContract || selectedContractId !== null) &&
        (!isInspectionReport || (selectedContractId !== null && selectedDate !== null && selectedEndDate !== null && !selectedDate.isAfter(selectedEndDate, 'day'))) &&
        (!isDailyCleaning || selectedContractId !== null) &&
        (!isDailyCleaning || zoneList.length <= 1 || selectAllZones || selectedZoneIds.length > 0 || selectedZoneId !== null);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>下載報告</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {!reportType && (
                        <Box sx={{ mt: 3 }}>
                            <InputLabel htmlFor="report-type-select">選擇報告類型</InputLabel>
                            <Box sx={{ mt: 1 }}>
                                <Select
                                    fullWidth
                                    value={selectedReportType}
                                    onChange={handleReportTypeChange}
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        請選擇報告類型
                                    </MenuItem>
                                    <MenuItem value="inspection_plastic_toilet">Plastic Toilet - Inspection Report</MenuItem>
                                    <MenuItem value="inspection_solar_toilet">Solar Toilet - Inspection Report</MenuItem>
                                </Select>
                            </Box>
                        </Box>
                    )}

                    {isInspectionReport && (
                        <Box sx={{ mt: 3 }}>
                            <InputLabel htmlFor="inspection-start-date-picker">開始日期</InputLabel>
                            <Box sx={{ mt: 1 }}>
                                <DatePicker
                                    onChange={handleDateChange}
                                    value={selectedDate}
                                    format="YYYY-MM-DD"
                                />
                            </Box>
                        </Box>
                    )}

                    {isInspectionReport && (
                        <Box sx={{ mt: 3 }}>
                            <InputLabel htmlFor="inspection-end-date-picker">結束日期</InputLabel>
                            <Box sx={{ mt: 1 }}>
                                <DatePicker
                                    onChange={handleEndDateChange}
                                    value={selectedEndDate}
                                    format="YYYY-MM-DD"
                                />
                            </Box>
                        </Box>
                    )}

                    {requiresDateInput && (
                        <Box sx={{ mt: 3 }}>
                            <InputLabel htmlFor="pdf-date-picker">選擇日期</InputLabel>
                            <Box sx={{ mt: 1 }}>
                                <DatePicker
                                    onChange={handleDateChange}
                                    value={selectedDate}
                                    format="YYYY-MM-DD"
                                />
                            </Box>
                        </Box>
                    )}

                    {requiresMonthInput && (
                        <Box sx={{ mt: 3 }}>
                            <InputLabel htmlFor="pdf-month-picker">選擇月份</InputLabel>
                            <Box sx={{ mt: 1 }}>
                                <DatePicker
                                    views={['year', 'month']}
                                    onChange={handleMonthChange}
                                    value={selectedMonth}
                                    format="YYYY-MM"
                                />
                            </Box>
                        </Box>
                    )}

                    {(isInspectionReport || isDailyCleaning) && (
                        <Box sx={{ mt: 3 }}>
                            <InputLabel htmlFor="contract-select">選擇合約</InputLabel>
                            <Box sx={{ mt: 1 }}>
                                <Select
                                    fullWidth
                                    value={selectedContractId || ""}
                                    onChange={(e) => setSelectedContractId(e.target.value as number)}
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        請選擇合約
                                    </MenuItem>
                                    {filteredContractList.map((contract) => (
                                        <MenuItem key={contract.contract_id} value={contract.contract_id}>
                                            {contract.contract}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                    )}

                    {isInspectionReport && (
                        <Box sx={{ mt: 3 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isInternalReport}
                                        onChange={(e) => setIsInternalReport(e.target.checked)}
                                    />
                                }
                                label={isInternalReport ? "內部報告" : "外部報告"}
                            />
                        </Box>
                    )}

                    {isDailyCleaning && selectedContractHasPeopleCounter && (
                        <Box sx={{ mt: 3 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includePeopleCounter}
                                        onChange={(e) => setIncludePeopleCounter(e.target.checked)}
                                    />
                                }
                                label="包含人流統計器"
                            />
                        </Box>
                    )}

                    {(isDailyCleaning || isInspectionReport) && selectedContractId && zoneList.length > 1 && (
                        <Box sx={{ mt: 3 }}>
                            <InputLabel htmlFor="zone-select">選擇區域</InputLabel>
                            <Box sx={{ mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectAllZones}
                                            onChange={(e) => handleSelectAllZones(e.target.checked)}
                                        />
                                    }
                                    label="選擇全部"
                                />
                                <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                                    {zoneList.map((zone) => (
                                        <FormControlLabel
                                            key={zone.zone_id}
                                            control={
                                                <Checkbox
                                                    checked={selectAllZones || selectedZoneIds.includes(zone.zone_id)}
                                                    onChange={() => handleZoneSelect(zone.zone_id)}
                                                />
                                            }
                                            label={zone.zone_name}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={() => {
                        const link = document.createElement("a");
                        link.href = "/ESS Convert PDF.exe";
                        link.download = "ESS Convert PDF.exe";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }}
                >
                    <ButtonTextGrid>
                        下載 ESS Convert PDF.exe
                    </ButtonTextGrid>
                </Button>
                <Button onClick={handleClose}>
                    <ButtonTextGrid>
                        取消
                    </ButtonTextGrid>
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    <ButtonTextGrid>
                        提交
                    </ButtonTextGrid>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

